import { Button, Stack } from "@mui/material";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as z from "zod";

import { useCreateNotificationHandlerNotificationHandlersPostMutation } from "api";
import { KeyValueInput, TextInput } from "components/forms";
import { INotificationHandlerWebhook } from "interfaces/notification.interface";

const webhookHandlerSchema = z.object({
  description: z.string(),
  configuration: z.object({
    url: z.url(),
    headers: z.array(
      z.object({
        key: z.string(),
        value: z.string(),
      }),
    ),
  }),
});

type WebhookHandlerSchema = z.infer<typeof webhookHandlerSchema>;

type TempWebhookProps = {
  disabled: boolean;
  zoneId: string;
  onClose: () => void;
  defaultValues: INotificationHandlerWebhook | null;
};

export function TempWebhook({
  disabled,
  zoneId,
  onClose,
  defaultValues,
}: TempWebhookProps) {
  const { t } = useTranslation();

  const [createHandler] =
    useCreateNotificationHandlerNotificationHandlersPostMutation();

  const { control, handleSubmit } = useForm<WebhookHandlerSchema>({
    resolver: zodResolver(webhookHandlerSchema),
    defaultValues: {
      description: defaultValues?.description ?? "",
      configuration: {
        url: defaultValues?.configuration.url,
        headers: Object.values(defaultValues?.configuration.headers ?? {}).map(
          ([key, value]) => ({ key, value }),
        ),
      },
    },
  });

  const onCreate = async (data: WebhookHandlerSchema) => {
    await createHandler({
      notificationHandlerCreate: {
        type: "webhook",
        zone_id: zoneId,
        enabled: true,
        description: data.description,
        configuration: {
          url: data.configuration.url,
          headers: Object.fromEntries(
            data.configuration.headers.map(({ key, value }) => [key, value]),
          ),
        },
      },
    });

    onClose();
  };

  return (
    <Stack
      component={"form"}
      onSubmit={handleSubmit(onCreate)}
      sx={{
        gap: 1,
      }}
    >
      <TextInput
        fieldName={"description"}
        control={control}
        label={t("Description")}
        placeholder={t("notification_webhook_handler_description_placeholder")}
        disabled={disabled}
        required
      />
      <TextInput
        fieldName={"configuration.url"}
        control={control}
        label={t("URL")}
        placeholder={t("notification_webhook_handler_url_placeholder")}
        disabled={disabled}
        required
      />
      <KeyValueInput
        fieldName={"configuration.headers"}
        control={control}
        label={t("headers_optional")}
        addLabel={t("add_header")}
        disabled={disabled}
      />
      <Stack
        direction={"row"}
        sx={{
          gap: 2,
        }}
      >
        {!defaultValues && <Button type={"submit"}>{t("Create")}</Button>}
        <Button variant={"text"} onClick={onClose}>
          {t("Cancel")}
        </Button>
      </Stack>
    </Stack>
  );
}
